import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormLabel from "@material-ui/core/FormLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";

import { getBackendUrl } from "../../config";

/**
 * Página pública do formulário — o que o cliente abre ao escanear o QR Code.
 *
 * Fica fora do layout autenticado e **não usa o cliente axios do CRM**: aquele
 * anexa o token do operador em toda requisição. Aqui não existe operador; usar
 * `fetch` puro garante que nenhuma credencial saia junto do envio de um
 * formulário aberto ao público.
 */
const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(4, 2),
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  sheet: {
    width: "100%",
    maxWidth: 620,
    padding: theme.spacing(4),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2.5) },
  },
  title: { fontWeight: 700 },
  description: { marginTop: theme.spacing(1), whiteSpace: "pre-wrap" },
  field: { marginTop: theme.spacing(2.5) },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
  },
  // Fora da tela, mas não `display:none`: bot que ignora CSS ainda encontra e
  // preenche o campo — é justamente esse o sinal que queremos capturar.
  honeypot: {
    position: "absolute",
    left: "-10000px",
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  center: { textAlign: "center", padding: theme.spacing(6, 2) },
  ok: { fontSize: 56, color: theme.palette.success.main },
  stepInfo: { marginTop: theme.spacing(1), display: "block" },
  // Layout autoral ocupa a tela inteira. Enquadrá-lo numa folha branca com
  // largura máxima prendia o fundo do autor numa caixa, com barra de rolagem
  // própria e a página do CRM aparecendo em volta — nada do que ele desenhou
  // sobrevive a isso.
  frameFull: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    border: 0,
    display: "block",
  },
}));

const PublicForm = () => {
  const classes = useStyles();
  const { slug } = useParams();

  const [form, setForm] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroFatal, setErroFatal] = useState("");
  const [precisaSenha, setPrecisaSenha] = useState(false);
  const [senha, setSenha] = useState("");

  const [valores, setValores] = useState({});
  const [erros, setErros] = useState({});
  const [etapa, setEtapa] = useState(1);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null);
  // Desafio anti-robô, quando o formulário pede. A pergunta e o token vêm do
  // servidor; a resposta volta com o envio.
  const [desafio, setDesafio] = useState(null);
  const [respostaDesafio, setRespostaDesafio] = useState("");
  const [erroDesafio, setErroDesafio] = useState("");

  // Marca de quando a página abriu, pelo relógio do servidor. É com ela que o
  // backend reconhece envio instantâneo de script.
  const abertoEm = useRef(null);
  const honeypotRef = useRef("");

  const base = useMemo(() => getBackendUrl().replace(/\/+$/, ""), []);

  const carregar = useCallback(
    async (senhaInformada) => {
      setCarregando(true);
      setErroFatal("");
      try {
        const resp = await fetch(`${base}/public/forms/${slug}`, {
          headers: senhaInformada ? { "X-Form-Password": senhaInformada } : {},
        });

        if (resp.status === 401) {
          const corpo = await resp.json().catch(() => ({}));
          setForm({ name: corpo.name });
          setPrecisaSenha(true);
          if (senhaInformada) setErroFatal("Senha incorreta.");
          return;
        }
        if (!resp.ok) {
          setErroFatal("Formulário não encontrado ou indisponível.");
          return;
        }

        const dados = await resp.json();
        abertoEm.current = dados.openedAt;
        setDesafio(dados.challenge || null);
        setRespostaDesafio("");
        setErroDesafio("");
        setForm(dados);
        setPrecisaSenha(false);
        setEtapa(1);
      } catch (err) {
        setErroFatal("Não foi possível carregar o formulário.");
      } finally {
        setCarregando(false);
      }
    },
    [base, slug]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const campos = form?.fields || [];
  const etapas = useMemo(() => {
    const nums = [...new Set(campos.map((c) => c.step || 1))].sort((a, b) => a - b);
    return nums.length ? nums : [1];
  }, [campos]);

  const camposDaEtapa = useMemo(
    () => (form?.multiStep ? campos.filter((c) => (c.step || 1) === etapa) : campos),
    [campos, etapa, form]
  );

  const mudar = (key, valor) => {
    setValores((atual) => ({ ...atual, [key]: valor }));
    setErros((atual) => ({ ...atual, [key]: undefined }));
  };

  /** Só o obrigatório em branco; o resto o servidor valida de verdade. */
  const faltandoNaEtapa = () =>
    camposDaEtapa
      .filter((c) => c.required && !String(valores[c.key] || "").trim())
      .reduce((acc, c) => ({ ...acc, [c.key]: `${c.label} é obrigatório` }), {});

  const avancar = () => {
    const faltando = faltandoNaEtapa();
    if (Object.keys(faltando).length) {
      setErros(faltando);
      return;
    }
    setEtapa((e) => Math.min(e + 1, etapas[etapas.length - 1]));
  };

  const enviar = async () => {
    const faltando = faltandoNaEtapa();
    if (Object.keys(faltando).length) {
      setErros(faltando);
      return;
    }

    setEnviando(true);
    setErros({});
    try {
      const resp = await fetch(`${base}/public/forms/${slug}/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(senha ? { "X-Form-Password": senha } : {}),
        },
        body: JSON.stringify({
          ...valores,
          [form.honeypotField]: honeypotRef.current,
          __openedAt: abertoEm.current,
          ...(desafio
            ? {
                __challengeToken: desafio.token,
                __challengeAnswer: respostaDesafio,
              }
            : {}),
        }),
      });

      const corpo = await resp.json().catch(() => ({}));

      if (corpo.challengeError) {
        setErroDesafio(corpo.challengeError);
        // Expirou: pega uma conta nova, senão a pessoa erra para sempre. O
        // que ela já digitou continua na tela.
        if (resp.status === 409) {
          const novo = await fetch(`${base}/public/forms/${slug}`)
            .then((r) => r.json())
            .catch(() => null);
          if (novo?.challenge) {
            setDesafio(novo.challenge);
            abertoEm.current = novo.openedAt;
          }
        }
        setRespostaDesafio("");
        return;
      }

      if (resp.status === 422 && corpo.errors) {
        setErros(corpo.errors);
        // Volta para a etapa do primeiro campo recusado, senão o erro fica
        // numa tela que a pessoa não está vendo.
        if (form.multiStep) {
          const primeiro = campos.find((c) => corpo.errors[c.key]);
          if (primeiro) setEtapa(primeiro.step || 1);
        }
        return;
      }
      if (!resp.ok) {
        setErroFatal(corpo.error || "Não foi possível enviar. Tente novamente.");
        return;
      }

      setEnviado(corpo.message || "Recebemos seus dados.");
      if (form.redirectUrl) {
        setTimeout(() => {
          window.location.href = form.redirectUrl;
        }, 2500);
      }
    } catch (err) {
      setErroFatal("Não foi possível enviar. Verifique sua conexão.");
    } finally {
      setEnviando(false);
    }
  };

  const renderCampo = (campo) => {
    const valor = valores[campo.key] || "";
    const erro = erros[campo.key];
    const comuns = {
      key: campo.key,
      fullWidth: true,
      variant: "outlined",
      label: campo.label,
      placeholder: campo.placeholder || undefined,
      helperText: erro || campo.helpText || undefined,
      error: !!erro,
      required: campo.required,
      className: classes.field,
      value: valor,
      onChange: (e) => mudar(campo.key, e.target.value),
    };

    if (campo.type === "textarea") {
      return <TextField {...comuns} multiline minRows={3} />;
    }
    if (campo.type === "select") {
      return (
        <TextField {...comuns} select>
          {(campo.options || []).map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (campo.type === "radio") {
      return (
        <div key={campo.key} className={classes.field}>
          <FormLabel error={!!erro}>{campo.label}</FormLabel>
          <RadioGroup
            value={valor}
            onChange={(e) => mudar(campo.key, e.target.value)}
          >
            {(campo.options || []).map((o) => (
              <FormControlLabel key={o} value={o} control={<Radio />} label={o} />
            ))}
          </RadioGroup>
          {(erro || campo.helpText) && (
            <Typography variant="caption" color={erro ? "error" : "textSecondary"}>
              {erro || campo.helpText}
            </Typography>
          )}
        </div>
      );
    }
    if (campo.type === "checkbox") {
      return (
        <div key={campo.key} className={classes.field}>
          <FormControlLabel
            control={
              <Checkbox
                checked={valor === "true"}
                onChange={(e) => mudar(campo.key, e.target.checked ? "true" : "false")}
              />
            }
            label={campo.label}
          />
          {(erro || campo.helpText) && (
            <Typography variant="caption" color={erro ? "error" : "textSecondary"} display="block">
              {erro || campo.helpText}
            </Typography>
          )}
        </div>
      );
    }

    const tipoHtml =
      { email: "email", phone: "tel", number: "number", date: "date" }[campo.type] ||
      "text";

    return (
      <TextField
        {...comuns}
        type={tipoHtml}
        InputLabelProps={campo.type === "date" ? { shrink: true } : undefined}
        inputProps={{ maxLength: campo.maxLength || undefined }}
      />
    );
  };

  if (carregando) {
    return (
      <div className={classes.page}>
        <div className={classes.center}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (precisaSenha) {
    return (
      <div className={classes.page}>
        <Paper className={classes.sheet} elevation={1}>
          <div className={classes.center}>
            <LockOutlinedIcon style={{ fontSize: 44 }} color="action" />
            <Typography variant="h6" className={classes.title}>
              {form?.name || "Formulário protegido"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Informe a senha de acesso para continuar.
            </Typography>
          </div>
          <TextField
            fullWidth
            type="password"
            variant="outlined"
            label="Senha de acesso"
            value={senha}
            error={!!erroFatal}
            helperText={erroFatal || undefined}
            onChange={(e) => setSenha(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && carregar(senha)}
          />
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => carregar(senha)}
              disabled={!senha}
            >
              Acessar
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  if (erroFatal && !form?.fields) {
    return (
      <div className={classes.page}>
        <Paper className={classes.sheet} elevation={1}>
          <div className={classes.center}>
            <Typography variant="h6">{erroFatal}</Typography>
          </div>
        </Paper>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className={classes.page}>
        <Paper className={classes.sheet} elevation={1}>
          <div className={classes.center}>
            <CheckCircleIcon className={classes.ok} />
            <Typography variant="h6" className={classes.title}>
              {enviado}
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  // Layout autoral: roda dentro de iframe `sandbox` SEM `allow-same-origin`.
  // O documento fica numa origem opaca — sem acesso a cookies, sem poder
  // chamar a API autenticada do CRM. Sem esse isolamento, um script escrito
  // num formulário e aberto por um operador logado alcançaria a sessão dele.
  if (form.layout === "custom" && form.customHtml) {
    return (
      <iframe
        title={form.name}
        className={classes.frameFull}
        sandbox="allow-forms allow-scripts allow-popups"
        srcDoc={montarDocumento(form, base, slug, senha)}
      />
    );
  }

  const ultimaEtapa = etapa === etapas[etapas.length - 1];

  return (
    <div className={classes.page}>
      <Paper className={classes.sheet} elevation={1}>
        <Typography variant="h6" className={classes.title}>
          {form.name}
        </Typography>
        {form.description && (
          <Typography variant="body2" color="textSecondary" className={classes.description}>
            {form.description}
          </Typography>
        )}

        {form.multiStep && etapas.length > 1 && (
          <>
            <LinearProgress
              variant="determinate"
              value={(etapas.indexOf(etapa) / (etapas.length - 1 || 1)) * 100}
              style={{ marginTop: 16 }}
            />
            <Typography variant="caption" color="textSecondary" className={classes.stepInfo}>
              Etapa {etapas.indexOf(etapa) + 1} de {etapas.length}
            </Typography>
          </>
        )}

        {camposDaEtapa.map(renderCampo)}

        {/* Armadilha para robô. Fora da tela, nunca anunciada a leitores de
            tela, e o backend recusa o envio se vier preenchida. */}
        <div className={classes.honeypot} aria-hidden="true">
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            name={form.honeypotField}
            onChange={(e) => {
              honeypotRef.current = e.target.value;
            }}
          />
        </div>

        {desafio && (!form.multiStep || ultimaEtapa) && (
          <TextField
            fullWidth
            variant="outlined"
            className={classes.field}
            label={desafio.question}
            placeholder="Digite o resultado"
            inputMode="numeric"
            value={respostaDesafio}
            error={!!erroDesafio}
            helperText={erroDesafio || "Confirmação rápida para evitar envios automáticos."}
            onChange={(e) => {
              setRespostaDesafio(e.target.value);
              setErroDesafio("");
            }}
          />
        )}

        {erroFatal && (
          <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
            {erroFatal}
          </Typography>
        )}

        <div className={classes.actions}>
          {form.multiStep && etapa > etapas[0] && (
            <Button onClick={() => setEtapa((e) => e - 1)} disabled={enviando}>
              Voltar
            </Button>
          )}
          {form.multiStep && !ultimaEtapa ? (
            <Button variant="contained" color="primary" onClick={avancar}>
              Continuar
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={enviar}
              disabled={enviando}
            >
              {enviando ? "Enviando…" : form.submitLabel || "Enviar"}
            </Button>
          )}
        </div>
      </Paper>
    </div>
  );
};

/**
 * Monta o documento do formulário autoral.
 *
 * O HTML/CSS/JS do autor entra aqui e roda isolado. O envio sai do próprio
 * iframe para o endpoint público — que não exige credencial, e por isso
 * funciona mesmo numa origem opaca.
 */
const montarDocumento = (form, base, slug, senha) => {
  const dados = JSON.stringify({
    base,
    slug,
    senha: senha || "",
    honeypotField: form.honeypotField,
    openedAt: form.openedAt,
    campos: form.fields || [],
    desafio: form.challenge || null,
  });

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
/* Base mínima, antes do CSS do autor — que pode sobrescrever tudo.
   Sem ela, quem esquecer de zerar a margem do body ganha faixas brancas nas
   bordas, e quem não declarar box-sizing vê o padding estourar os 100vh e
   criar barra de rolagem numa página que cabia. */
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; min-height: 100%; }
</style>
<style>${form.customCss || ""}</style>
</head>
<body>
${form.customHtml}
<script>
// Ponte mínima disponível ao autor do formulário: monta o envio com as
// proteções que o backend espera (honeypot e momento de abertura) para que
// ele não precise conhecer o protocolo.
(function () {
  var cfg = ${dados};
  window.CRMForm = {
    campos: cfg.campos,
    // Pergunta do desafio, para quem quiser desenhar o proprio campo.
    desafio: cfg.desafio ? { pergunta: cfg.desafio.question } : null,

    enviar: function (valores) {
      var corpo = Object.assign({}, valores);
      corpo[cfg.honeypotField] = corpo[cfg.honeypotField] || "";
      corpo.__openedAt = cfg.openedAt;

      // Desafio ligado e o autor nao coletou a resposta: perguntamos aqui.
      // Sem isto, o formulario com HTML proprio seria recusado sem que a
      // pessoa entendesse o motivo — o desafio existiria so no servidor.
      if (cfg.desafio && !corpo.__challengeAnswer) {
        return perguntarDesafio().then(function (resposta) {
          if (resposta === null) return { ok: false, status: 0, body: {} };
          corpo.__challengeAnswer = resposta;
          return despachar(corpo);
        });
      }

      if (cfg.desafio) corpo.__challengeToken = cfg.desafio.token;
      return despachar(corpo);
    }
  };

  function despachar(corpo) {
    if (cfg.desafio) corpo.__challengeToken = cfg.desafio.token;
    var headers = { "Content-Type": "application/json" };
    if (cfg.senha) headers["X-Form-Password"] = cfg.senha;
    return fetch(cfg.base + "/public/forms/" + cfg.slug + "/submit", {
      method: "POST",
      headers: headers,
      body: JSON.stringify(corpo)
    }).then(function (r) {
      return r.json().then(function (j) {
        return { ok: r.ok, status: r.status, body: j };
      });
    });
  }

  // Caixa sobreposta, neutra de proposito: ela cobre o desenho do autor por
  // alguns segundos, entao nao deve competir com ele.
  function perguntarDesafio() {
    return new Promise(function (resolve) {
      var fundo = document.createElement("div");
      fundo.setAttribute("style",
        "position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;" +
        "align-items:center;justify-content:center;padding:20px;z-index:2147483647");

      var caixa = document.createElement("div");
      caixa.setAttribute("style",
        "background:#fff;border-radius:14px;padding:24px;max-width:340px;width:100%;" +
        "font-family:-apple-system,'Segoe UI',Roboto,sans-serif;box-shadow:0 20px 50px rgba(0,0,0,.3)");

      var titulo = document.createElement("p");
      titulo.textContent = cfg.desafio.question;
      titulo.setAttribute("style","margin:0 0 4px;font-size:17px;font-weight:700;color:#0f172a");

      var ajuda = document.createElement("p");
      ajuda.textContent = "Confirmação rápida para evitar envios automáticos.";
      ajuda.setAttribute("style","margin:0 0 14px;font-size:13px;color:#64748b;line-height:1.4");

      var campo = document.createElement("input");
      campo.type = "text";
      campo.inputMode = "numeric";
      campo.setAttribute("style",
        "width:100%;padding:12px 14px;font-size:16px;border:1.5px solid #e2e8f0;" +
        "border-radius:10px;outline:none;box-sizing:border-box;font-family:inherit");

      var erro = document.createElement("p");
      erro.setAttribute("style","margin:8px 0 0;font-size:13px;color:#dc2626;min-height:16px");

      var ok = document.createElement("button");
      ok.type = "button";
      ok.textContent = "Confirmar";
      ok.setAttribute("style",
        "width:100%;margin-top:14px;padding:13px;font-size:15px;font-weight:700;color:#fff;" +
        "background:#7c3aed;border:0;border-radius:10px;cursor:pointer;font-family:inherit");

      function confirmar() {
        var v = campo.value.trim();
        if (!v) { erro.textContent = "Digite o resultado."; campo.focus(); return; }
        document.body.removeChild(fundo);
        resolve(v);
      }

      ok.addEventListener("click", confirmar);
      campo.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); confirmar(); }
      });

      caixa.appendChild(titulo); caixa.appendChild(ajuda); caixa.appendChild(campo);
      caixa.appendChild(erro); caixa.appendChild(ok);
      fundo.appendChild(caixa);
      document.body.appendChild(fundo);
      campo.focus();
    });
  }
})();
</script>
<script>${form.customJs || ""}</script>
</body>
</html>`;
};

export default PublicForm;
